// src/context/CareGapContext/CareGapContext.js
import React, { createContext, useEffect, useState, useCallback } from "react";
import { useValidToken } from "../../hooks/useValidToken";
import secureStorage from "../../utils/security/secureStorage";

// ------- Servicios CareGap (listado + detalle + update) -------
import getCareGaps from "../../utils/services/Caregaps/getCareGaps";
import getCareGapDetail from "../../utils/services/Caregaps/getCareGapDetail";
import updateCareGapByUser from "../../utils/services/Caregaps/updateCareGapByUser";
import getPatientCareGaps from "../../utils/services/Caregaps/getPatientCareGaps";

// ------- Servicios Evidencia -------
import getEvidenceByCareGap from "../../utils/services/Caregaps/getEvidenceByCareGap";
import insertEvidence from "../../utils/services/Caregaps/insertEvidence";
import updateEvidence from "../../utils/services/Caregaps/updateEvidence";

// ------- Servicios Outreach -------
import getOutreachByCareGap from "../../utils/services/Caregaps/getOutreachByCareGap";
import insertOutreach from "../../utils/services/Caregaps/insertOutreach";
import updateOutreach from "../../utils/services/Caregaps/updateOutreach";

// ------- Servicios Audit -------
import getAuditByCareGap from "../../utils/services/Caregaps/getAuditByCareGap";
import insertAudit from "../../utils/services/Caregaps/insertAudit";

// ------- Servicios Workflow Status -------
import getWorkflowStatuses from "../../utils/services/Caregaps/getWorkflowStatuses";

/**
 * Normaliza IDs al tipo que espera cada endpoint del backend.
 *
 * IMPORTANTE: los endpoints de CareGap son inconsistentes en cuanto al tipo
 * que esperan en los campos de empleado:
 *   - POST /outreach        → `employee`             debe ser STRING
 *   - POST /evidence        → `uploadedBy`           debe ser STRING
 *   - PUT  /api/CareGap     → `newAssignedEmployeeId` debe ser NUMBER
 *   - POST /audit           → `performedBy`          debe ser NUMBER
 *
 * Para evitar errores 400 ("The JSON value could not be converted to ..."),
 * usamos helpers explícitos para cada caso.
 */
const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toStringIdOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  return String(v);
};

export const CareGapContext = createContext();

export const CareGapProvider = ({ children }) => {
  const { isValid, authLoading } = useValidToken();

  // ===================== Filtros del listado general =====================
  const [filters, setFilters] = useState({
    search: "",
    sourceStatusCode: "",
    workflowStatusCode: "",
    uploadId: "",
    careGapNameSearch: "",
    careGapTypeId: null,
    page: 1,
    pageSize: 50,
    orderBy: "",
  });

  // ===================== Estado de listado =====================
  const [careGaps, setCareGaps] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // ===================== Estado de detalle =====================
  const [selectedCareGapId, setSelectedCareGapId] = useState(null);
  const [careGapDetail, setCareGapDetail] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [audit, setAudit] = useState([]);

  // ===================== Estado de Care Gaps por paciente (v3) =====================
  const [patientCareGaps, setPatientCareGaps] = useState([]);
  const [patientCareGapsTotalCount, setPatientCareGapsTotalCount] = useState(0);

  // ===================== Workflow Statuses =====================
  const [workflowStatuses, setWorkflowStatuses] = useState([]);

  // ===================== Estado general =====================
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canCallApi = useCallback(() => !authLoading && isValid, [authLoading, isValid]);

  // Helper: obtiene el employeeId del usuario autenticado como número.
  // Para endpoints que esperan string, el caller convierte con toStringIdOrNull.
  const getCurrentEmployeeId = () => {
    try {
      const u = secureStorage.getItem("user");
      return toNumberOrNull(u?.employeeId ?? u?.employee);
    } catch {
      return null;
    }
  };

  // =======================================================================
  //                               LISTADO
  // =======================================================================

  const loadCareGaps = async (overrideFilters = {}) => {
    if (!canCallApi()) return;

    const params = { ...filters, ...overrideFilters };

    setLoading(true);
    setError(null);

    try {
      const result = await getCareGaps(params);
      const rawItems = Array.isArray(result?.data) ? result.data : [];

      const items = rawItems.map((row) => ({
        // IDs
        id: row.careGapId,
        careGapId: row.careGapId,

        // textos principales
        careGapName: row.careGapName,
        patientName: row.patientFullName,
        patientFullName: row.patientFullName,
        cipId: row.cipId,
        pcpName: row.pcpName,

        // estados
        status: row.workflowStatusCode,
        workflowStatusCode: row.workflowStatusCode,
        workflowStatus: row.workflowStatus,
        sourceStatusCode: row.sourceStatusCode,
        sourceStatus: row.sourceStatus,

        // fechas
        lastUpdated: row.lastUpdateDate,
        createdDate: row.createdDate,
        serviceClosureDate: row.serviceDateOfClosure,
        measurementYear: row.measurementYear,
        refreshWeek: row.measurementYearRefreshWeek,

        // otros
        historicallyNonCompliant: row.historicallyNonCompliant,
        assignedEmployeeId: row.assignedEmployeeId,
        priority: row.priority,
        evidenceCount: row.evidenceCount,
        outreachCount: row.outreachCount,
        legacySourceTable: row.legacySourceTable,
        legacyUploadId: row.legacyUploadId,
      }));

      setCareGaps(items);

      // result: { data, page, pageSize, total, totalPages }
      setTotalCount(
        typeof result?.total === "number" ? result.total : items.length
      );
    } catch (err) {
      console.error("Error loading care gaps:", err);
      setError(err);
      setCareGaps([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowStatuses = async () => {
    if (!canCallApi()) return;
    try {
      const data = await getWorkflowStatuses();
      const items = Array.isArray(data) ? data : data?.data ?? [];
      setWorkflowStatuses(items);
    } catch (err) {
      console.error("Error loading workflow statuses:", err);
    }
  };

  // ✅ Cambiado: SOLO carga inicial cuando el token está listo.
  // Ya NO vuelve a cargar por cada cambio en filters.
  useEffect(() => {
    if (!canCallApi()) return;
    loadCareGaps();
    loadWorkflowStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isValid]);

  // =======================================================================
  //                      LISTADO POR PACIENTE (v3)
  // =======================================================================

  const loadCareGapsByPatient = useCallback(
    async (cipId, page = 1, pageSize = 100) => {
      if (!canCallApi()) return;
      if (!cipId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await getPatientCareGaps(cipId, page, pageSize);
        const rawItems = Array.isArray(result?.data) ? result.data : [];

        const items = rawItems.map((row) => ({
          // IDs
          id: row.careGapId,
          careGapId: row.careGapId,

          // textos principales
          careGapName: row.careGapName,
          patientName: row.patientFullName,
          patientFullName: row.patientFullName,
          cipId: row.cipId,
          pcpName: row.pcpName,

          // estados
          status: row.workflowStatusCode,
          workflowStatusCode: row.workflowStatusCode,
          workflowStatus: row.workflowStatus,
          sourceStatusCode: row.sourceStatusCode,
          sourceStatus: row.sourceStatus,

          // fechas
          lastUpdated: row.lastUpdateDate,
          createdDate: row.createdDate,
          submittedDate: row.submittedDate,
          serviceClosureDate: row.serviceDateOfClosure,
          measurementYear: row.measurementYear,
          refreshWeek: row.measurementYearRefreshWeek,

          // otros
          historicallyNonCompliant: row.historicallyNonCompliant,
          assignedEmployeeId: row.assignedEmployeeId,
          priority: row.priority,
          evidenceCount: row.evidenceCount,
          outreachCount: row.outreachCount,
          legacySourceTable: row.legacySourceTable,
          legacyUploadId: row.legacyUploadId,
        }));

        setPatientCareGaps(items);
        setPatientCareGapsTotalCount(
          typeof result?.total === "number" ? result.total : items.length
        );
      } catch (err) {
        console.error("Error loading care gaps by patient:", err);
        setError(err);
        setPatientCareGaps([]);
        setPatientCareGapsTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [canCallApi]
  );

  // ✅ Nuevo: aplicar filtros y consultar SOLO cuando se llama explícitamente
  const applyFilters = async (nextFilters) => {
    const finalFilters = nextFilters || filters;
    setFilters(finalFilters);
    await loadCareGaps(finalFilters);
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      sourceStatusCode: "",
      workflowStatusCode: "",
      uploadId: "",
      careGapNameSearch: "",
      careGapTypeId: null,
      page: 1,
      pageSize: 50,
      orderBy: "",
    });
  };

  // ✅ Nuevo: paginación explícita (como ya no hay auto-fetch)
  const goToPage = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
    await loadCareGaps({ page });
  };

  const changePageSize = async (pageSize) => {
    const size = Number(pageSize) || 50;
    setFilters((prev) => ({ ...prev, pageSize: size, page: 1 }));
    await loadCareGaps({ pageSize: size, page: 1 });
  };

  // (Si quieres mantener estos métodos, los dejamos, pero OJO:
  //  ya no disparan consulta automáticamente.)
  const setPage = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  // =======================================================================
  //                            DETALLE COMPLETO
  // =======================================================================

  const loadCareGapDetail = async (careGapId) => {
    if (!canCallApi() || !careGapId) return;

    try {
      const detail = await getCareGapDetail(careGapId);
      setCareGapDetail(detail || null);
    } catch (err) {
      console.error("Error loading care gap detail:", err);
      setError(err);
      setCareGapDetail(null);
    }
  };

  const loadEvidence = async (careGapId) => {
    if (!canCallApi() || !careGapId) return;

    try {
      const res = await getEvidenceByCareGap(careGapId);
      const items = Array.isArray(res?.data) ? res.data : [];
      setEvidence(items);
    } catch (err) {
      console.error("Error loading evidence:", err);
      setError(err);
      setEvidence([]);
    }
  };

  const loadOutreach = async (careGapId) => {
    if (!canCallApi() || !careGapId) return;

    try {
      const res = await getOutreachByCareGap(careGapId);
      const items = Array.isArray(res?.data) ? res.data : [];
      setOutreach(items);
    } catch (err) {
      console.error("Error loading outreach:", err);
      setError(err);
      setOutreach([]);
    }
  };

  const loadAudit = async (careGapId) => {
    if (!canCallApi() || !careGapId) return;

    try {
      const res = await getAuditByCareGap(careGapId);
      const items = Array.isArray(res?.data) ? res.data : [];
      setAudit(items);
    } catch (err) {
      console.error("Error loading audit:", err);
      setError(err);
      setAudit([]);
    }
  };

  const selectCareGap = async (careGapId) => {
    setSelectedCareGapId(careGapId);

    if (!careGapId) {
      setCareGapDetail(null);
      setEvidence([]);
      setOutreach([]);
      setAudit([]);
      return;
    }

    if (!canCallApi()) return;

    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        loadCareGapDetail(careGapId),
        loadEvidence(careGapId),
        loadOutreach(careGapId),
        loadAudit(careGapId),
      ]);
    } catch (err) {
      console.error("Error selecting care gap:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // =======================================================================
  //                              ACCIONES
  // =======================================================================

  // Evidencia (POST /evidence)
  const addEvidence = async (evidenceData) => {
    if (!canCallApi()) return;

    const careGapId = evidenceData.careGapId || selectedCareGapId;
    if (!careGapId) return;

    setError(null);

    try {
      // El endpoint POST /evidence espera `uploadedBy` como STRING.
      const uploadedById =
        toNumberOrNull(evidenceData.uploadedBy) ?? getCurrentEmployeeId();
      const payload = {
        careGapId: toNumberOrNull(careGapId),
        legacyFileId: evidenceData.legacyFileId || "",
        fileName: evidenceData.fileName || "",
        filePath: evidenceData.filePath || "",
        uploadedBy: toStringIdOrNull(uploadedById),
      };

      await insertEvidence(payload);
      await loadEvidence(careGapId);
      return { ok: true, message: "Evidence submitted successfully." };
    } catch (err) {
      console.error("Error inserting evidence:", err, err?.response?.data);
      setError(err);
      return {
        ok: false,
        message:
          err?.response?.data?.title ||
          err?.message ||
          "Could not submit evidence.",
      };
    }
  };

  const editEvidence = async (evidenceData) => {
    if (!canCallApi() || !selectedCareGapId) return;

    setError(null);

    try {
      await updateEvidence(evidenceData);
      await loadEvidence(selectedCareGapId);
    } catch (err) {
      console.error("Error updating evidence:", err);
      setError(err);
    }
  };

  // Outreach (POST /outreach)
  const addOutreach = async (outreachData) => {
    if (!canCallApi()) return;

    const careGapId = outreachData.careGapId || selectedCareGapId;
    if (!careGapId) return;

    setError(null);

    try {
      // El endpoint POST /outreach espera `employee` como STRING.
      const employeeId =
        toNumberOrNull(outreachData.employee) ?? getCurrentEmployeeId();
      const payload = {
        careGapId: toNumberOrNull(careGapId),
        contactMethod: outreachData.contactMethod,
        attemptDate: outreachData.attemptDate || new Date().toISOString(),
        employee: toStringIdOrNull(employeeId),
        result: outreachData.result || "",
        notes: outreachData.notes || "",
      };

      await insertOutreach(payload);
      await loadOutreach(careGapId);
      return { ok: true, message: "Outreach logged successfully." };
    } catch (err) {
      console.error("Error inserting outreach:", err, err?.response?.data);
      setError(err);
      return {
        ok: false,
        message:
          err?.response?.data?.title ||
          err?.message ||
          "Could not log outreach.",
      };
    }
  };

  const editOutreach = async (outreachData) => {
    if (!canCallApi() || !selectedCareGapId) return;

    setError(null);

    try {
      await updateOutreach(outreachData);
      await loadOutreach(selectedCareGapId);
    } catch (err) {
      console.error("Error updating outreach:", err);
      setError(err);
    }
  };

  // Audit (POST /audit) — espera IDs como NUMBER.
  const addAudit = async (auditData) => {
    if (!canCallApi()) return;

    const careGapId = auditData.careGapId || selectedCareGapId;
    if (!careGapId) return;

    setError(null);

    try {
      const payload = {
        ...auditData,
        careGapId: toNumberOrNull(careGapId),
        ...(auditData.performedBy !== undefined && {
          performedBy: toNumberOrNull(auditData.performedBy) ?? getCurrentEmployeeId(),
        }),
        ...(auditData.employeeId !== undefined && {
          employeeId: toNumberOrNull(auditData.employeeId) ?? getCurrentEmployeeId(),
        }),
        ...(auditData.userId !== undefined && {
          userId: toNumberOrNull(auditData.userId) ?? getCurrentEmployeeId(),
        }),
      };
      await insertAudit(payload);
      await loadAudit(careGapId);
    } catch (err) {
      console.error("Error inserting audit:", err, err?.response?.data);
      setError(err);
    }
  };

  // Actualizar CareGap (PUT /api/CareGap)
  const updateCareGap = async (careGapData) => {
    if (!canCallApi()) return;

    setError(null);

    try {
      // PUT /api/CareGap espera todos los IDs como NUMBER.
      const payload = {
        ...careGapData,
        careGapId: toNumberOrNull(careGapData.careGapId || selectedCareGapId),
        newWorkflowStatusId: toNumberOrNull(careGapData.newWorkflowStatusId),
        newAssignedEmployeeId:
          toNumberOrNull(careGapData.newAssignedEmployeeId) ??
          getCurrentEmployeeId() ??
          0,
        newPriority: toNumberOrNull(careGapData.newPriority) ?? 0,
      };

      await updateCareGapByUser(payload);

      // refrescar listado y detalle
      await loadCareGaps();
      if (selectedCareGapId) {
        await loadCareGapDetail(selectedCareGapId);
      }
      return { ok: true, message: "Care gap updated successfully." };
    } catch (err) {
      console.error("Error updating care gap:", err, err?.response?.data);
      setError(err);
      return {
        ok: false,
        message:
          err?.response?.data?.title ||
          err?.message ||
          "Could not update care gap.",
      };
    }
  };

  // =======================================================================
  //                               PROVIDER
  // =======================================================================

  return (
    <CareGapContext.Provider
      value={{
        // listado
        careGaps,
        totalCount,
        filters,
        setFilters,
        resetFilters,
        loadCareGaps,

        // ✅ nuevos (para consultar solo cuando el usuario lo pida)
        applyFilters,
        goToPage,
        changePageSize,

        // listado por paciente (v3)
        patientCareGaps,
        patientCareGapsTotalCount,
        loadCareGapsByPatient,

        // detalle
        selectedCareGapId,
        selectCareGap,
        careGapDetail,
        evidence,
        outreach,
        audit,

        // workflow statuses
        workflowStatuses,
        loadWorkflowStatuses,

        // acciones
        addEvidence,
        editEvidence,
        addOutreach,
        editOutreach,
        addAudit,
        updateCareGap,

        // estado global
        loading,
        error,
      }}
    >
      {children}
    </CareGapContext.Provider>
  );
};
